import * as React from 'react';
import { useCallback, useMemo } from 'react';

import AddCircleIcon from '@mui/icons-material/AddCircle';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import RemoveIcon from '@mui/icons-material/Remove';
import { Box, Divider, IconButton, Stack, Typography } from '@mui/material';

import { EntityMetadataModel, ParameterMetadata } from '../../../../api/api';
import { ExpressionModel } from '../../Models/ExpressionModel';
import { IVariableScope } from '../../Models/VariableScopes/IVariableScope';
import ExpressionVariableScopeCalculatorService from '../../Services/ExpressionVariableScopeCalculatorService';
import ParameterPlaceholder from '../Parameters/ParameterPlaceholder';

type ExpressionPlaceholderCollectionParamEditorProps = {
	param: ParameterMetadata;
	showLabel?: boolean;
	paramValues: (ExpressionModel | string | number | boolean | undefined)[];
	expression: ExpressionModel;
	onExpressionChange: (e: ExpressionModel) => void;
	itemScopes: (IVariableScope | undefined)[] | undefined;
	baseParamScope: IVariableScope | undefined;
	variableScope: IVariableScope | undefined;
	expressionScopeCalculator: ExpressionVariableScopeCalculatorService | null;
	isEditable?: boolean;
	sharedContextMenuId: string;
	expectedObjectType: EntityMetadataModel | undefined;
};

export default function ExpressionPlaceholderCollectionParamEditor({
	param,
	showLabel = true,
	paramValues,
	expression,
	onExpressionChange,
	itemScopes,
	baseParamScope,
	variableScope,
	expressionScopeCalculator,
	isEditable,
	sharedContextMenuId,
	expectedObjectType,
}: ExpressionPlaceholderCollectionParamEditorProps) {
	const handleAddCollectionItem = useCallback(() => {
		const e: ExpressionModel = { ...expression };
		e[param.name] = [...(expression[param.name] ?? []), undefined];
		onExpressionChange(e);
	}, [expression, param.name, onExpressionChange]);

	const handleRemoveCollectionItem = useCallback(
		(index: number) => {
			const e: ExpressionModel = { ...expression };
			const currentValues = [...(expression[param.name] ?? [])];
			currentValues.splice(index, 1);
			e[param.name] = currentValues;
			onExpressionChange(e);
		},
		[expression, param.name, onExpressionChange],
	);

	const handleMoveItemUp = useCallback(
		(index: number) => {
			if (index === 0) return;
			const e: ExpressionModel = { ...expression };
			const currentValues = [...(expression[param.name] ?? [])];
			[currentValues[index - 1], currentValues[index]] = [currentValues[index], currentValues[index - 1]];
			e[param.name] = currentValues;
			onExpressionChange(e);
		},
		[expression, param.name, onExpressionChange],
	);

	const handleMoveItemDown = useCallback(
		(index: number) => {
			if (index === paramValues.length - 1) return;
			const e: ExpressionModel = { ...expression };
			const currentValues = [...(expression[param.name] ?? [])];
			[currentValues[index], currentValues[index + 1]] = [currentValues[index + 1], currentValues[index]];
			e[param.name] = currentValues;
			onExpressionChange(e);
		},
		[expression, param.name, onExpressionChange, paramValues.length],
	);

	// Resolve the result type of item[0] to constrain items 1..n to the same type.
	// Only applied when param.expectedType has no explicit type constraint.
	const firstItemResolvedType = useMemo(() => {
		if (param.expectedType?.type || param.expectedType?.typeRef || param.expectedType?.typeJsonSchema) return undefined;
		const firstItemExpression = paramValues.length > 0 ? (paramValues[0] as ExpressionModel | undefined) : undefined;
		const firstItemScope = baseParamScope ?? variableScope;
		if (!firstItemExpression || !firstItemScope || !expressionScopeCalculator) return undefined;
		return expressionScopeCalculator.analyzeExpressionType(firstItemExpression, firstItemScope);
	}, [param.expectedType, paramValues, baseParamScope, variableScope, expressionScopeCalculator]);

	return (
		<Stack
			direction="column"
			spacing={0.5}
			sx={{
				width: '100%',
				minWidth: 0,
				padding: 1,
				backgroundColor: 'background.paper',
				borderRadius: 1,
				border: '1px solid',
				borderColor: 'divider',
			}}
		>
			{showLabel && <Typography
				variant="caption"
				color="text.primary"
				fontWeight="bold"
				title={param.description || ''}
				sx={{ cursor: param.description ? 'help' : 'default' }}
			>
				{param.displayName || param.name}:
			</Typography>}
			{paramValues.map((paramValue: ExpressionModel | string | number | boolean | undefined, i: number) => {
				const setParamValue = (newParamValue: ExpressionModel | string | number | undefined) => {
					const e: ExpressionModel = { ...expression };
					const v = [...paramValues];
					v[i] = newParamValue;
					e[param.name] = v;
					onExpressionChange(e);
				};

				const paramVariableScope = itemScopes?.[i] ?? baseParamScope ?? variableScope;

				const constrainedParam: ParameterMetadata = i > 0 && firstItemResolvedType ? { ...param, expectedType: firstItemResolvedType } : param;

				return (
					<React.Fragment key={`${param.name}-${i}`}>
						{i > 0 && <Divider sx={{ my: 1, borderColor: 'divider' }} />}
						<Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
							<Box sx={{ flex: 1, minWidth: 0 }}>
							<ParameterPlaceholder
								value={paramValue}
								onValueChange={setParamValue}
								parameterMetadata={constrainedParam}
								parameterEnum={i > 0 && firstItemResolvedType?.referenceEnum ? firstItemResolvedType.referenceEnum : undefined}
								variableScope={paramVariableScope}
								isEditable={isEditable}
								contextMenuId={sharedContextMenuId}
								expectedObjectType={expectedObjectType}
							/>
							</Box>
							<Stack direction="row" spacing={0.5} sx={{ marginLeft: 'auto', flexShrink: 0, pt: 0.5 }}>
								<IconButton
									size="small"
									onClick={(e) => {
										e.stopPropagation();
										handleMoveItemUp(i);
									}}
									disabled={i === 0}
									title="Move item up"
									sx={{ padding: '2px' }}
								>
									<ArrowUpwardIcon fontSize="small" />
								</IconButton>
								<IconButton
									size="small"
									onClick={(e) => {
										e.stopPropagation();
										handleMoveItemDown(i);
									}}
									disabled={i === paramValues.length - 1}
									title="Move item down"
									sx={{ padding: '2px' }}
								>
									<ArrowDownwardIcon fontSize="small" />
								</IconButton>
								<IconButton
									size="small"
									onClick={(e) => {
										e.stopPropagation();
										handleRemoveCollectionItem(i);
									}}
									disabled={paramValues.length <= 1}
									title={`Remove item from ${param.displayName || param.name}`}
									sx={{ padding: '2px' }}
								>
									<RemoveIcon fontSize="small" />
								</IconButton>
							</Stack>
						</Stack>
					</React.Fragment>
				);
			})}
			<Divider sx={{ my: 1, borderColor: 'divider' }} />
			<IconButton
				size="small"
				onClick={(e) => {
					e.stopPropagation();
					handleAddCollectionItem();
				}}
				title={`Add item to ${param.displayName || param.name}`}
				sx={{ alignSelf: 'center', padding: '2px' }}
			>
				<AddCircleIcon fontSize="small" />
			</IconButton>
		</Stack>
	);
}
